// src/pages/CourseAccessPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import CourseModule from '../components/CourseModule';
import CourseMaterials from '../components/CourseMaterials';
import HelpButton from '../components/HelpButton';
import { Module } from '../types';
import { getCourseModules } from '../services/courseService';
import { updateCourseProgress, getUserCertificate } from '../services/authService'; // Import getUserCertificate
import { AlertCircle } from 'lucide-react';

const CourseAccessPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [allCompleted, setAllCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentModule, setCurrentModule] = useState<string | null>(null);
  const [userCertificate, setUserCertificate] = useState<any | null>(null); // New state for certificate

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (user.isDisabled === true) {
      navigate('/payment-required');
      return;
    }

    const loadPageData = async () => { // Renamed function to load all data
      try {
        const moduleData = await getCourseModules();

        const updatedModules = moduleData.map(module => ({
          ...module,
          completed: user.completedModules?.includes(module.id) || false
        }));

        setModules(updatedModules);

        const areAllCompleted = user.courseProgress?.allCompleted || false;
        setAllCompleted(areAllCompleted);

        // Fetch user certificate
        const certificate = await getUserCertificate(user.id);
        setUserCertificate(certificate);

        setError(null);
      } catch (error) {
        console.error('Error loading page data:', error);
        setError('Fehler beim Laden der Daten. Bitte versuchen Sie es später erneut.');
      } finally {
        setLoading(false);
      }
    };

    loadPageData(); // Call the new function
  }, [user, navigate]);

  const handleModuleComplete = useCallback(async (moduleId: string, isComplete: boolean) => {
    if (!user) return;

    setError(null);
    try {
      // Update UI optimistically
      const updatedModules = modules.map(module =>
        module.id === moduleId ? { ...module, completed: isComplete } : module
      );
      setModules(updatedModules);

      const updatedUser = await updateCourseProgress(user.id, moduleId, isComplete);

      if (!updatedUser) {
        throw new Error('Keine Antwort vom Server erhalten');
      }

      // Update local state with server response
      const allModulesCompleted = updatedUser.courseProgress.allCompleted;
      setAllCompleted(allModulesCompleted);

      // Update modules with server state
      setModules(prevModules => prevModules.map(module => ({
        ...module,
        completed: updatedUser.courseProgress.modulesCompleted[module.id] || false
      })));

      // Re-fetch certificate status in case completion triggered it
      const certificate = await getUserCertificate(user.id);
      setUserCertificate(certificate);

    } catch (error) {
      console.error('Error updating module completion:', error);

      // Revert optimistic updates
      setModules(prevModules => prevModules.map(module => ({
        ...module,
        completed: user.courseProgress?.modulesCompleted[module.id] || false
      })));
      setAllCompleted(user.courseProgress?.allCompleted || false);

      setError('Fehler beim Aktualisieren des Moduls. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.');
    }
  }, [user, modules, updateCourseProgress, getUserCertificate, setModules, setAllCompleted, setUserCertificate, setError]);

  const handleStartTest = () => {
    navigate('/test');
  };

  const handleViewCertificate = () => {
    if (userCertificate) {
      navigate('/zertifikat', { 
        state: { 
          certData: {
            ...userCertificate,
            // Ensure all required fields are present
            id: userCertificate.id,
            testResultId: userCertificate.testResultId
          }
        } 
      });
    }
  };

  const getIsModuleLocked = (moduleIndex: number) => {
    if (moduleIndex === 0) return false;
    const previousModule = modules[moduleIndex - 1];
    return previousModule && !previousModule.completed;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Ihr KI-Kompetenznachweis
          </h1>
          <p className="text-gray-600 mb-8">
            Absolvieren Sie alle Module, um den Abschlusstest freizuschalten und Ihr Zertifikat zu erhalten.
          </p>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center text-red-800">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Kursmodule</h2>

            <div className="space-y-4">
              {modules.map((module, index) => (
                <CourseModule
                  key={module.id}
                  module={module}
                  onComplete={handleModuleComplete}
                  isLocked={getIsModuleLocked(index)}
                  onExpand={() => setCurrentModule(module.title)}
                />
              ))}
            </div>
          </div>

          <CourseMaterials />

          {userCertificate && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center mb-8">
              <h3 className="text-xl font-semibold text-blue-800 mb-3">
                Ihr Zertifikat ist verfügbar!
              </h3>
              <p className="text-blue-700 mb-4">
                Sie haben den Test erfolgreich abgeschlossen und Ihr Zertifikat erhalten.
              </p>
              <button
                onClick={handleViewCertificate}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-md transition-colors"
              >
                Zertifikat anzeigen
              </button>
            </div>
          )}

          {allCompleted && !userCertificate && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center mb-8">
              <h3 className="text-xl font-semibold text-green-800 mb-3">
                Glückwunsch! Sie haben alle Module erfolgreich abgeschlossen.
              </h3>
              <p className="text-green-700 mb-4">
                Sie können jetzt den Abschlusstest starten und Ihr Zertifikat erhalten.
              </p>
              <button
                onClick={handleStartTest}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-md transition-colors"
              >
                Abschlusstest starten
              </button>
            </div>
          )}

          {!allCompleted && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">
                Ihr Fortschritt
              </h3>
              <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                <div
                  className="bg-blue-600 h-4 rounded-full"
                  style={{
                    width: `${(modules.filter(m => m.completed).length / modules.length) * 100}%`
                  }}
                ></div>
              </div>
              <p className="text-blue-700">
                Sie haben {modules.filter(m => m.completed).length} von {modules.length} Modulen abgeschlossen.
                {modules.filter(m => m.completed).length > 0 ?
                  ' Setzen Sie Ihren Kurs fort.' :
                  ' Beginnen Sie mit dem ersten Modul.'}
              </p>
            </div>
          )}
        </div>
      </div>

      <HelpButton currentModule={currentModule || undefined} />
    </div>
  );
};

export default CourseAccessPage;
